/**
 * useQuotesCatchup
 *
 * Runs in the QuotesScreen once per browser session.
 * Catches up any flights that transitioned while the app was closed:
 *
 *  IN_TRANSIT → COMPLETED   aircraft landed at expected destination
 *  IN_TRANSIT → DIVERTED    aircraft landed at a different airport
 *
 * A flight is "past-due" when its arrival_date has passed but its DB status
 * is still IN_TRANSIT, meaning the FlightDetailScreen was never open to catch
 * the landing in real time.
 *
 * After each flight update the quote status cascade runs via deriveQuoteStatus.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus } from '@/features/track/utils/flightStatusUtils';

const CATCHUP_KEY = 'gtj_quotes_catchup_done'; // sessionStorage flag

export function useQuotesCatchup() {
  const queryClient = useQueryClient();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (sessionStorage.getItem(CATCHUP_KEY)) return; // already ran this session

    runCatchup(queryClient).then(() => {
      sessionStorage.setItem(CATCHUP_KEY, '1');
    });
  }, [queryClient]);
}

async function runCatchup(queryClient) {
  try {
    // Fetch all flights still marked IN_TRANSIT
    const data = await apiClient.getFlights({
      flight_status: 'IN_TRANSIT',
      include: 'quotes,aircrafts',
    });
    const inTransit = Array.isArray(data) ? data : [];

    // Only care about flights whose expected arrival has passed
    const now = new Date();
    const pastDue = inTransit.filter((f) => {
      const arrival = f.arrival_date ? new Date(f.arrival_date) : null;
      return arrival && arrival < now;
    });

    if (!pastDue.length) return;

    console.log(`[QuotesCatchup] Checking ${pastDue.length} past-due IN_TRANSIT flight(s)`);

    // Resolve each via FR24 summary
    const resolved = await Promise.allSettled(
      pastDue.map(async (flight) => {
        const tailNumber = flight.aircraft?.tail_number;
        if (!tailNumber) return { flight, newStatus: 'COMPLETED' }; // can't verify — assume completed

        // Search ±6 h around the expected arrival
        const arrTime = new Date(flight.arrival_date);
        const from = new Date(arrTime.getTime() - 6 * 60 * 60 * 1000).toISOString();
        const to = new Date(arrTime.getTime() + 6 * 60 * 60 * 1000).toISOString();

        const summaryData = await apiClient.getFlightSummary(tailNumber, from, to);
        const summaries = Array.isArray(summaryData) ? summaryData : (summaryData?.data || []);

        if (!summaries.length) {
          // Aircraft not found in summary — treat as landed at destination
          return { flight, newStatus: 'COMPLETED' };
        }

        const latest = summaries[summaries.length - 1];
        const landedAt = (latest?.dest_icao || latest?.dest_iata || '').toUpperCase();
        const expected = (flight.arrival_airport || '').toUpperCase();

        if (!landedAt || !expected || landedAt === expected) {
          return { flight, newStatus: 'COMPLETED' };
        }

        return { flight, newStatus: 'DIVERTED' };
      })
    );

    const updates = resolved
      .filter((r) => r.status === 'fulfilled' && r.value)
      .map((r) => r.value);

    if (!updates.length) return;

    // Apply updates and cascade to quotes
    await Promise.allSettled(
      updates.map(async ({ flight, newStatus }) => {
        try {
          console.log(`[QuotesCatchup] Flight ${flight.id}: IN_TRANSIT → ${newStatus}`);
          await apiClient.patchFlight(flight.id, { flight_status: newStatus });

          const quoteId = flight.quote?.id || flight.quote_id;
          if (quoteId) {
            const newQuoteStatus = await deriveQuoteStatus(quoteId, flight.id, newStatus);
            if (newQuoteStatus) {
              console.log(`[QuotesCatchup] Quote ${quoteId}: → ${newQuoteStatus}`);
              await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
            }
          }
        } catch (err) {
          console.error(`[QuotesCatchup] Update failed for flight ${flight.id}:`, err);
        }
      })
    );

    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['flights'] });
  } catch (err) {
    console.error('[QuotesCatchup] Catchup failed:', err);
  }
}
